import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	DatePicker,
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useIsTouchDevice,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	doesNotExceedLength,
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNotNil } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { IntegrationFlowCapabilityKind } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { DateRangeFormData, InputDataFormData } from "./common";

export type ConfigureInputDataDialogProps = {
	initialData: InputDataFormData;
	onClose: () => void;
	onSave: (inputData: InputDataFormData) => void;
};

export const ConfigureInputDataDialog = (props: ConfigureInputDataDialogProps): ReactElement => {
	const {
		initialData,
		onSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<InputDataFormData>(initialData);

	const isTouch: boolean = useIsTouchDevice();
	const userTimezone: TimeZone = useUserTimezone();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<InputDataFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				setIsBusy(false);
				onSave(formData);
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose,
			onSave
		]
	);

	return (
		<Dialog
			trackingId="integration-configure-input-data-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_AddInputData")}
			/>
			<Dialog.Body>
				<p>{localize("_PleaseInputTheDataThatWillBeUsedAsTheValueForThePlaceholderInfo")}</p>
				<Form onSubmit={saveHandler}>
					{formHandler.when(
						hasSearchCapability,
						(attachable): ReactElement => (
							<FormItem
								label={localize("_SearchQuery")}
								isRequired
							>
								<TextInput
									{...attachable.for("searchTerm").attachToTextInput()}
									placeholder={localize("_EnterSearchQuery")}
									autoFocus={!isTouch
										? "focus"
										: undefined
									}
								/>
								<ValidationError {...attachable.for("searchTerm").attachToValidationError()} />
							</FormItem>
						)
					)}
					{formHandler.when(
						hasDateRangeCapability,
						(attachable): ReactElement => {
							const dateRangeAttachable: IValueInputAttachable<DateRangeFormData> = attachable.for("dateRange");

							return (
								<>
									<FormItem
										label={localize("_StartDateTime")}
										isRequired
									>
										<DatePicker
											{...dateRangeAttachable.for("start").attachToDatePicker()}
											timezone={userTimezone}
											autoFocus={!isTouch && !hasSearchCapability(formData)}
										/>
										<ValidationError {...dateRangeAttachable.for("start").attachToValidationError()} />
									</FormItem>
									<FormItem
										label={localize("_EndDateTime")}
										isRequired
									>
										<DatePicker
											{...dateRangeAttachable.for("end").attachToDatePicker()}
											timezone={userTimezone}
											minDate={dateRangeAttachable.for("start").value ?? undefined}
										/>
										<ValidationError {...dateRangeAttachable.for("end").attachToValidationError()} />
									</FormItem>
								</>
							);
						}
					)}
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={saveHandler}
						text={localize("_Save")}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ConfigureInputDataDialog.displayName = "ConfigureInputDataDialog";

function hasSearchCapability(formData: InputDataFormData): boolean {
	return formData.capabilities
		.includes(IntegrationFlowCapabilityKind.Search);
}

const validationConfiguration: IValidationConfiguration<InputDataFormData> = createValidationConfiguration<InputDataFormData>();

validationConfiguration
	.when(hasSearchCapability)
	.forScalar("searchTerm")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const dateRangeValidationConfiguration: IObjectValidatorBuilder<DateRangeFormData> = validationConfiguration
	.when(hasDateRangeCapability)
	.forObject("dateRange");

dateRangeValidationConfiguration.forScalar("start")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);

dateRangeValidationConfiguration.forScalar("end")
	.add(
		isNotNullNorUndefined(),
		isValidDate()
	);

dateRangeValidationConfiguration
	.when(isEndNotAfterStart)
	.forScalar("end")
	.add(
		inline((): InputValidationResult => {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: localize("_EndDateTimeMustBeGreaterThanStartDateTime")
			};
		})
	);

export type ConfigureInputDataDialogInitialData = Omit<
	ConfigureInputDataDialogProps,
	"onSave" | "onClose"
>;

export function useConfigureInputDataDialog(): [
	element: ReactElement | null,
	open: (props: ConfigureInputDataDialogInitialData) => Promise<TryResult<InputDataFormData>>
] {
	return useDataInputDialog(
		(props, onCancellation, onConfirmation) => (
			<ConfigureInputDataDialog
				{...props}
				onSave={onConfirmation}
				onClose={onCancellation}
			/>
		)
	);
}

function hasDateRangeCapability(formData: InputDataFormData): boolean {
	return formData.capabilities
		.includes(IntegrationFlowCapabilityKind.DateRange);
}

function isEndNotAfterStart(dateRange: DateRangeFormData): boolean {
	const {
		start,
		end
	} = dateRange;

	return isNotNil(start)
		&& isNotNil(end)
		&& end.getTime() <= start.getTime();
}